import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import LangToggle from "@/components/LangToggle";
import { useLang } from "@/i18n/LangContext";
import { useAuth } from "@/lib/AuthContext";
import api, { formatApiError } from "@/lib/api";

const SIDE_IMG =
  "https://images.unsplash.com/photo-1727409163340-b20cfa3fa3ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxlbGVnYW50JTIwZXVyb3BlYW4lMjBob21lJTIwaW50ZXJpb3IlMjBuYXR1cmFsJTIwbGlnaHR8ZW58MHx8fHwxNzgwNjk1NjU5fDA&ixlib=rb-4.1.0&q=85";

export function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      // Resume a checkout intent that brought the user to /login.
      const intent = params.get("intent");
      const plan = params.get("plan");
      const provider = params.get("provider") || "lemonsqueezy";
      if (intent === "checkout" && plan) {
        try {
          const endpoint =
            provider === "paypal"
              ? "/payments/paypal/subscribe"
              : "/payments/lemonsqueezy/checkout";
          const { data } = await api.post(endpoint, {
            plan,
            origin_url: window.location.origin,
          });
          window.location.href = data.url;
          return;
        } catch (resumeErr) {
          // Fall through to default redirect if checkout fails to resume.
          setError(formatApiError(resumeErr.response?.data?.detail) || resumeErr.message);
        }
      }
      navigate(u.onboarded ? "/app" : "/onboarding");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t("auth.loginTitle")} sub={t("auth.loginSub")}>
      <form onSubmit={onSubmit} className="space-y-8" data-testid="login-form">
        <Field label={t("auth.email")}>
          <input
            type="email"
            required
            autoComplete="email"
            className="line-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input"
          />
        </Field>
        <Field label={t("auth.password")}>
          <input
            type="password"
            required
            autoComplete="current-password"
            className="line-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password-input"
          />
        </Field>
        {error && (
          <p className="text-[var(--error)] text-sm" data-testid="login-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="login-submit-button">
          {loading ? "…" : t("auth.submitLogin")}
        </button>
        <p className="text-center label-cap text-[var(--ink-faint)]" data-testid="login-demo-hint">
          {process.env.REACT_APP_SHOW_DEMO_HINT === "true"
            ? t("auth.demoHint", {
                email: process.env.REACT_APP_DEMO_EMAIL || "",
                password: process.env.REACT_APP_DEMO_PASSWORD || "",
              })
            : ""}
        </p>
        <p className="text-center">
          <Link to="/register" className="label-cap hover:text-[var(--ink)]" data-testid="login-to-register">
            {t("auth.toRegister")}
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const { t, lang } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [language, setLanguage] = useState(lang);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!acceptTerms) return;
    setError("");
    setLoading(true);
    try {
      await register({ name, email, password, language, accept_terms: true });
      navigate("/onboarding");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t("auth.registerTitle")} sub={t("auth.registerSub")}>
      <form onSubmit={onSubmit} className="space-y-8" data-testid="register-form">
        <Field label={t("auth.name")}>
          <input
            required
            className="line-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="register-name-input"
          />
        </Field>
        <Field label={t("auth.email")}>
          <input
            required
            type="email"
            autoComplete="email"
            className="line-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="register-email-input"
          />
        </Field>
        <Field label={t("auth.password")}>
          <input
            required
            type="password"
            minLength={6}
            autoComplete="new-password"
            className="line-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="register-password-input"
          />
        </Field>
        <Field label={t("auth.language")}>
          <div className="flex gap-3 mt-2">
            <ChipButton
              active={language === "pt"}
              onClick={() => setLanguage("pt")}
              testId="register-lang-pt"
            >
              Português
            </ChipButton>
            <ChipButton
              active={language === "en"}
              onClick={() => setLanguage("en")}
              testId="register-lang-en"
            >
              English
            </ChipButton>
          </div>
        </Field>
        {error && (
          <p className="text-[var(--error)] text-sm" data-testid="register-error">
            {error}
          </p>
        )}
        <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="register-accept-terms-label">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className="mt-1 h-4 w-4 cursor-pointer accent-[var(--ink)]"
            data-testid="register-accept-terms-checkbox"
            required
          />
          <span className="text-[var(--ink-soft)] leading-snug">
            {t("auth.consentLeadIn")}{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[var(--ink)]"
              data-testid="register-terms-link"
            >
              {t("auth.consentTerms")}
            </a>
            {" "}{t("auth.consentAnd")}{" "}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[var(--ink)]"
              data-testid="register-privacy-link"
            >
              {t("auth.consentPrivacy")}
            </a>
            .
          </span>
        </label>
        <button type="submit" disabled={loading || !acceptTerms} className="btn-primary w-full justify-center" data-testid="register-submit-button">
          {loading ? "…" : t("auth.submitRegister")}
        </button>
        <p className="text-center">
          <Link to="/login" className="label-cap hover:text-[var(--ink)]" data-testid="register-to-login">
            {t("auth.toLogin")}
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}

function AuthShell({ title, sub, children }) {
  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[var(--bg)]">
      <div className="hidden md:block relative">
        <img src={SIDE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[var(--ink)]/30" />
        <div className="absolute top-8 left-10">
          <Link to="/" className="font-display text-2xl text-[var(--bg)]" data-testid="auth-brand">
            Mordomo
          </Link>
        </div>
        <div className="absolute bottom-12 left-10 right-10">
          <p className="label-cap text-[var(--bg)]/80">— Ao seu dispor</p>
          <p className="font-display-italic text-3xl mt-4 text-[var(--bg)] leading-snug max-w-md">
            «Ouvi-te no onboarding. Trato a partir de agora.»
          </p>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="flex justify-end p-6">
          <LangToggle />
        </div>
        <div className="flex-1 flex items-center justify-center px-6 md:px-12 pb-12">
          <div className="w-full max-w-md">
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight">{title}</h1>
            <p className="font-display-italic text-[var(--ink-soft)] mt-3 text-lg">{sub}</p>
            <div className="mt-12">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-cap">{label}</span>
      {children}
    </label>
  );
}

function ChipButton({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-5 py-2 text-[12px] tracking-monocle uppercase border transition-colors duration-300 ${
        active
          ? "bg-[var(--ink)] text-[var(--bg)] border-[var(--ink)]"
          : "bg-transparent text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--ink)] hover:text-[var(--ink)]"
      }`}
    >
      {children}
    </button>
  );
}
